/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { DndContext, TouchSensor, MouseSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Dispatch, FC, RefObject, useState } from 'react';

import { getSeparatorPairs } from './LiveAgentSessionUtils';
import { LiveAgentStepComponent } from './LiveAgentStepComponent';
import { LiveAgentStepSeparator } from './LiveAgentStepSeparator';
import { Step } from './LiveAgentTypes';

import { coordinateGetter } from '@/lib/utils';

// a step group is defined as an unbroken streak of steps that share a common "intentHash" parameter
// if that intent hash exists outside of a group, then the whole group is discarded
// multiple groups can exist in the same list of steps
// a group can be a single step
// a step can't be part of multiple groups

export const LiveAgentSteps: FC<{
    parentIndex?: number;
    steps: Step[];
    dispatch?: Dispatch<any>;
    executeStep?: (step: Step) => void;
    removeStep?: (step: Step) => void;
    highlightSelectors?: (
        selectors: { [key: string]: { selector: string; unique: boolean } } | null,
        force?: boolean,
    ) => void;
    isDisabled?: boolean;
    queue?: number[];
    setQueue?: (index: number) => void;
    resetQueue?: () => void;
    inert?: boolean;
    variables: { [key: string]: string };
    precomputedVariables?: { [key: string]: string };
    editVariables?: (action: 'add' | 'remove', payload: { [key: string]: string }) => void;
    openStep?: string | null;
    updateOpenStep?: (stepId: string, force?: boolean) => void;
    fullScreenContainer?: RefObject<HTMLDivElement>;
    hidePlaywright?: boolean;
}> = ({ steps, parentIndex, isDisabled, queue, ...props }) => {
    const [draggedId, setDraggedId] = useState<string | number | null>(null);

    const sensors = useSensors(
        useSensor(MouseSensor),
        useSensor(TouchSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter,
        }),
    );

    const separatorPairs = getSeparatorPairs(steps);
    const firstSeparatorIndices = separatorPairs.map((pair) => pair.pair[0]);

    return (
        <DndContext
            modifiers={[restrictToVerticalAxis]}
            sensors={sensors}
            onDragStart={({ active }) => {
                if (props.dispatch) {
                    if (!active) {
                        return;
                    }

                    setDraggedId(active.id);
                }
            }}
            onDragEnd={({ over }) => {
                if (props.dispatch) {
                    setDraggedId(null);

                    if (!over) return;

                    if (draggedId === over.id) return;

                    const from = steps.findIndex((step) => step.id === draggedId);
                    let to = steps.findIndex((step) => step.id === over.id);

                    const fromStep = steps[from];
                    if (fromStep.kind === 'separator') {
                        const hash = fromStep.intentHash;
                        const indexOfHash = separatorPairs.findIndex((pair) => pair.intentHash === hash);
                        const lowerBound = indexOfHash === 0 ? 0 : separatorPairs[indexOfHash - 1].pair[1] + 1;
                        const upperBound =
                            indexOfHash === separatorPairs.length - 1
                                ? steps.length
                                : separatorPairs[indexOfHash + 1].pair[0] - 1;

                        // we are trying to move a separator to a place where it doesn't belong,
                        // so we put it to the nearest correct place
                        if (to < lowerBound) {
                            to = lowerBound;
                        } else if (to > upperBound) {
                            to = upperBound;
                        }
                    }

                    props.dispatch({
                        type: 'reorder',
                        payload: { from, to, parentIndex: over.data.current?.parentIndex },
                    });
                }
            }}
        >
            <SortableContext items={steps} strategy={verticalListSortingStrategy}>
                <div className="pt-2 space-y-1">
                    {steps
                        .filter((step) => step.kind !== 'recordedActions' || !step.recording)
                        .map((step, index) => {
                            if (step.kind === 'separator') {
                                return (
                                    <LiveAgentStepSeparator
                                        {...props}
                                        isFirst={firstSeparatorIndices.includes(index)}
                                        key={step.id}
                                        step={step}
                                        index={index}
                                        parentIndex={parentIndex}
                                        queued={queue && queue.some((q) => q === index)}
                                    />
                                );
                            }
                            return (
                                <LiveAgentStepComponent
                                    {...props}
                                    key={step.id}
                                    step={step}
                                    index={index}
                                    parentIndex={parentIndex}
                                    queue={queue}
                                    queued={queue && queue.some((q) => q === index)}
                                    isDisabled={
                                        isDisabled ||
                                        steps.some((s) => {
                                            if (s.kind === 'block') {
                                                return s.steps?.some((ss) => ss.running);
                                            }
                                            return s.running;
                                        })
                                    }
                                />
                            );
                        })}
                </div>
            </SortableContext>
        </DndContext>
    );
};
